import { SVGProps } from 'react';
import cn from 'classnames';
import Link from 'next/link';

export type Theme = 'DEFAULT' | 'CORNFLOWER' | 'GREEN' | 'RED' | 'YELLOW';

interface DocCardProps {
  theme?: Theme;
  title: string;
  description: string;
  href: string;
  icon: (props: SVGProps<SVGSVGElement>) => JSX.Element;
  quickLinks?: {
    text: string;
    href: string;
  }[];
}

export const backgroundTheme = {
  DEFAULT: `from-gray-400 to-gray-500`,
  CORNFLOWER: `from-primary-500 to-primary-600`,
  GREEN: `from-green-400 to-green-500`,
  RED: `from-red-400 to-red-500`,
  YELLOW: `from-yellow-400 to-yellow-500`,
};

const bulletTheme = {
  DEFAULT: `text-gray-500`,
  CORNFLOWER: `text-primary-500`,
  GREEN: `text-green-500`,
  RED: `text-red-500`,
  YELLOW: `text-yellow-400`,
};

export default function DocCard({
  theme = `DEFAULT`,
  title,
  description,
  href,
  icon: Icon,
  quickLinks = [],
}: DocCardProps) {
  return (
    <div className="rounded-md shadow-sm overflow-hidden">
      <div
        className={cn([
          `hidden bg-gradient-to-br h-56`,
          backgroundTheme[theme],
        ])}
      />
      <div className="bg-white rounded-md p-6 md:p-8 border border-primary-100 space-y-4">
        <div className="flex items-center">
          <div
            className={cn([
              `flex-shrink-0 w-7 h-7 mr-3 rounded-md text-white flex items-center justify-center bg-gradient-to-br`,
              backgroundTheme[theme],
            ])}
          >
            <Icon />
          </div>
          <Link href={href} prefetch={false}>
            <a className="block w-full !no-underline">
              <h3 className="text-primary-700 text-lg md:text-xl font-semibold flex items-center space-between !my-0 !py-0 !border-none">
                {title}
                <svg
                  className="fill-current w-7 h-7 ml-auto"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                >
                  <path fill="none" d="M0 0h24v24H0z" />
                  <path d="M13.172 12l-4.95-4.95 1.414-1.414L16 12l-6.364 6.364-1.414-1.414z" />
                </svg>
              </h3>
            </a>
          </Link>
        </div>
        <p className="text-gray-500 text-sm">{description}</p>
        {quickLinks && (
          <ul
            className={cn([
              `list-inside text-sm space-y-1.5`,
              bulletTheme[theme],
            ])}
          >
            {quickLinks.map((link, index) => (
              <li key={index}>
                <Link href={link.href} prefetch={false}>
                  <a className="text-gray-500 hover:text-primary-800 !no-underline">
                    {link.text}
                  </a>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
